import React from 'react';
import { Outlet, useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { visitTypes } from 'context/visitContext';
import moment from 'moment';
import { questionnairesQuery } from 'pages/PatientRecords/PatientRecords';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isEmpty } from 'ramda';
import { syncPatientWithPhoton } from 'api/api';
import { capitalize, isRole } from 'utils/utils';
import { Sidetab } from '@typeform/embed-react';
import { useAppContext } from '../../context/appContext';
import { paths } from '../../routes';
import { HTTPError, Patient, QuestionAnswers, Visit } from '../../types/types';
import readAWSS3File from '../../utils/readAWSS3File';
import Icon from '../Icon/Icon';
import Image from '../Image/Image';

const TabIds = [
  'about',
  'medical-history',
  'medication-history',
  'visit-history',
  'labs',
  'notes',
  'symptoms',
  'chat',
  'treatment-plan',
] as const;

type TabItem = {
  id: (typeof TabIds)[number];
  name: string;
};

function VisitRecords() {
  const [{ user, patients }, dispatch] = useAppContext();
  const [syncedAvatarPhoto, setSyncedAvatarPhoto] = React.useState('');
  const { visitId } = useParams<{ visitId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const patientId = params.get('patient');
  const from = params.get('from');

  const patient = (patients || []).find(p => p.id === patientId);

  /** We are calling the questionnairesQuery() in order to get the visit creation date */
  const { data } = useQuery({
    ...questionnairesQuery({ id: visitId }),
    enabled: !!(isEmpty(patient?.questionnaires) && visitId),
  });

  const questionnaire = React.useMemo(() => {
    const questionnaires = (data || patient?.questionnaires || []) as QuestionAnswers[];
    const findQuestionnaire = questionnaires.find(
      fndQuestionnaire => fndQuestionnaire.id === visitId,
    );

    if (!findQuestionnaire) return undefined;

    const visit = visitTypes.find(
      fndVisit => fndVisit.type === findQuestionnaire.type?.toLocaleUpperCase(),
    ) as Visit;

    return {
      ...findQuestionnaire,
      visit,
    };
  }, [data, patient?.questionnaires, visitId]);

  const activeTab =
    !location.pathname.split('/').slice(-1)[0] ||
    location.pathname.split('/').slice(-1)[0] === visitId
      ? 'about'
      : location.pathname.split('/').slice(-1)[0];

  const { mutate: syncPatientWithPhotonFn } = useMutation(
    (variables: { patientId: string }) => syncPatientWithPhoton(variables.patientId),
    {
      onSuccess: (result, variables) => {
        const { photonId } = result;
        const updatePatients = patients.map(fndPatient => ({
          ...fndPatient,
          ...(fndPatient.id === variables.patientId && {
            photonId,
          }),
        })) satisfies Patient[];
        dispatch({ type: 'SET_ASSIGNED_PATIENTS', payload: updatePatients });
      },
      onError: (error: HTTPError) => {
        console.error('patient photon sync error', error?.message);
      },
    },
  );

  React.useEffect(() => {
    if (!patient?.id || isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN')) return;
    syncPatientWithPhotonFn({ patientId: patient.id });
  }, [patient?.id, syncPatientWithPhotonFn, user?.roles]);

  React.useEffect(() => {
    if (patient && !patient.signedAvatar) {
      (async function syncAvatarPhoto() {
        setSyncedAvatarPhoto(await readAWSS3File('avatar', patient.profilePicture));
      })();
    }
  }, [dispatch, patient]);

  React.useEffect(() => {
    if (isMobile) {
      window.scrollTo({
        behavior: 'smooth',
        top: 0,
      });
    }
  }, [activeTab]);

  const getTabURL = (fndTab: string) => {
    if (!questionnaire?.visit) return '#';

    const tabURL = `${paths.VISIT_RECORDS_URL_PATH.replace(':visitId', visitId || 'null')}${
      fndTab === 'about' ? '' : `/${fndTab}`
    }?type=${questionnaire?.visit.type.toLocaleLowerCase()}&patient=${patientId}${
      from ? `&from=${encodeURIComponent(from)}` : ''
    }`;

    return tabURL;
  };

  if (!patient) return null;

  return (
    <>
      <div className="flex flex-col tablet:-mx-10">
        <div className="flex flex-col py-4 px-4 tablet:px-8">
          <div className="flex items-center tablet:space-x-8 tablet:px-10 tablet:py-0">
            <button
              onClick={() => navigate(from || paths.HOME_URL_PATH)}
              type="button"
              className="hidden tablet:flex flex-row items-center"
            >
              <Icon id="goBack" className="w-8 h-8" />
            </button>
            <div className="flex items-center space-x-5 shrink-0">
              <Image
                className="h-[70px] w-[70px] tablet:h-[80px] tablet:w-[80px] rounded-full shrink-0"
                src={
                  patient.signedAvatar ||
                  syncedAvatarPhoto ||
                  'image_patient_avatar_placeholder.png'
                }
                alt={patient ? `${patient.firstName} ${patient.lastName}` : 'River Patient'}
                bgColor={patient.avatarPlaceholderColor}
              />
              <div>
                <h1 className="text-river-black font-medium text-[20px]">
                  {patient ? `${patient.firstName} ${patient.lastName}` : 'River Patient'}
                </h1>
                {questionnaire ? (
                  <>
                    <p className="text-sm mt-1">{questionnaire.visit?.name} Visit</p>
                    <span className="text-zinc-400 text-[13px] mt-0 block leading-none">
                      On: {moment(questionnaire.createdAt, 'YYYY-MM-DD').format('DD MMM YYYY')}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
          </div>
          <hr className="hidden laptop:block border-b-[1px] border-t-0 border-[#e9ecef] tablet:mx-28 mt-7 mb-7" />
          <div className="tablet:mx-28">
            <ul
              className="pt-7 pb-0 border-b-[1px] border-[#e9ecef]
              mb-6 laptop:pt-0 laptop:border-none laptop:mb-0 flex flex-row space-x-4 overflow-x-auto no-scrollbar"
              role="tablist"
            >
              {PAGE_TABS.map((fndTab, index) => (
                <li
                  key={fndTab.id}
                  className="flex flex-row space-x-4 items-center whitespace-nowrap"
                >
                  <Link
                    role="tab"
                    aria-controls={fndTab.id}
                    aria-selected="false"
                    className={`text-[17px] pb-1 laptop:pb-0 tablet:text-[15px] text-zinc-400 ${
                      activeTab === fndTab.id &&
                      '!text-river-blue font-medium border-b-2 border-river-blue laptop:border-none'
                    }`}
                    to={getTabURL(fndTab.id)}
                  >
                    {fndTab.name}
                  </Link>
                  {index !== PAGE_TABS.length - 1 && (
                    <Icon id="arrowNext" className="w-[11px] h-[11px] text-zinc-400" />
                  )}
                </li>
              ))}
            </ul>
            <div className="flex-auto tablet:block" id={activeTab}>
              <Outlet context={{ patient }} />
            </div>
          </div>
        </div>
      </div>

      {/* {!isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN') && (
        <Sidetab id="feCiUj4k" buttonText="Support&nbsp;&nbsp;Ticket" />
      )} */}
    </>
  );
}

export const PAGE_TABS: TabItem[] = TabIds.map(id => ({
  id,
  name: capitalize(id.replace(/-/g, ' ')),
}));

export default VisitRecords;
